import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { estimateService } from '../../services/estimateService';

const CreateEstimatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();

  const initialLineItem = { description: '', quantity: 1, unitPrice: 0, total: 0 };

  const [formData, setFormData] = useState({
    clientId: null,
    serviceRequestId: null,
    originatingQuoteId: null,
    estimateNumber: `EST-${Date.now()}`,
    estimateDate: new Date().toISOString().split('T')[0],
    expiryDate: '',
    lineItems: [initialLineItem],
    subTotal: 0,
    taxRate: 0.0825,
    taxAmount: 0,
    totalAmount: 0,
    status: 'draft',
    notes: '',
    termsAndConditions: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [isConverting, setIsConverting] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      if (!token) return;
      try {
        const response = await fetch('/api/users?role=client', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch clients');
        const data = await response.json();
        if (data.status === 'success') {
          setClients(data.data.users || []);
        } else {
          throw new Error(data.message || 'Could not parse client data');
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
      }
    };
    fetchClients();

    if (location.state && location.state.quoteToConvert) {
      const quote = location.state.quoteToConvert;
      setIsConverting(true);
      setFormData(prev => ({
        ...prev,
        clientId: quote.clientId || quote.client?.id || null,
        originatingQuoteId: quote.id,
        lineItems: quote.lineItems?.map(item => ({ 
            description: item.description, 
            quantity: parseFloat(item.quantity || 1),
            unitPrice: parseFloat(item.unitPrice || 0),
            total: parseFloat(item.total || 0)
        })) || [initialLineItem],
        notes: quote.notes || '',
        termsAndConditions: quote.termsAndConditions || '',
      }));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [token, location, navigate]);

  useEffect(() => {
    let currentSubTotal = 0;
    formData.lineItems.forEach(item => {
      currentSubTotal += parseFloat(item.total || 0);
    });
    const currentTaxAmount = currentSubTotal * parseFloat(formData.taxRate || 0);
    const currentTotalAmount = currentSubTotal + currentTaxAmount;

    setFormData(prev => ({
      ...prev,
      subTotal: currentSubTotal,
      taxAmount: currentTaxAmount,
      totalAmount: currentTotalAmount,
    }));
  }, [formData.lineItems, formData.taxRate]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
       ...prev,
       [name]: type === 'number' ? parseFloat(value) : value 
    }));
  };

  const handleLineItemChange = (index, e) => {
    const { name, value, type } = e.target;
    const updatedLineItems = [...formData.lineItems];
    const item = updatedLineItems[index];
    item[name] = type === 'number' ? parseFloat(value) : value;
    
    if (name === 'quantity' || name === 'unitPrice') {
      item.total = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
    }
    updatedLineItems[index] = item;
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: 1, unitPrice: 0, total: 0 }],
    }));
  };

  const removeLineItem = (index) => {
    const updatedLineItems = [...formData.lineItems];
    updatedLineItems.splice(index, 1);
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (!formData.clientId) {
        setError('Please select a client.');
        setIsLoading(false);
        return;
    }
    if (formData.lineItems.some(item => !item.description || item.quantity <= 0 || item.unitPrice <= 0)) {
        setError('Please ensure all line items have a description, and positive quantity and unit price.');
        setIsLoading(false);
        return;
    }    const payload = {
        ...formData,
        clientId: parseInt(formData.clientId, 10),
        estimateDate: new Date(formData.estimateDate).toISOString(),
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null,
        // Ensure financial values are properly formatted
        subTotal: formData.subTotal.toString(),
        taxAmount: formData.taxAmount.toString(),
        totalAmount: formData.totalAmount.toString(),
        taxRate: parseFloat(formData.taxRate) // Keep as number for schema validation
    };

    try {
      const response = await estimateService.createEstimate(payload);
      
      alert('Estimate created successfully!');
      if (response && response.data && response.data.estimate && response.data.estimate.id) {
        navigate(`/admin/estimates/view/${response.data.estimate.id}`);
      } else if (response && response.id) {
        // Handle direct response format
        navigate(`/admin/estimates/view/${response.id}`);
      } else {
        navigate('/admin/estimates');
      }

    } catch (err) {
      console.error('Error creating estimate:', err);
      setError(err.message || 'An error occurred while creating the estimate.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-brand-text-dark">
          {isConverting ? 'Convert Quote to Estimate' : 'Create New Estimate'}
        </h1>
        <button 
          onClick={() => navigate(-1)} 
          className="text-sm text-blue-600 hover:text-blue-800 mt-2"
        >
          &larr; Back to Estimates
        </button>
      </header>

      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6 md:p-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="mb-6">
            <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">Client</label>
            <select
                name="clientId"
                id="clientId"
                value={formData.clientId || ''}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
            >
                <option value="" disabled>Select a client</option>
                {clients.map(client => (
                    <option key={client.id} value={client.id}>
                        {client.username} ({client.email})
                    </option>
                ))}
            </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="estimateNumber" className="block text-sm font-medium text-gray-700 mb-1">Estimate Number</label>
            <input type="text" name="estimateNumber" id="estimateNumber" value={formData.estimateNumber} onChange={handleChange} className="mt-1 block w-full input-class" required />
          </div>
          <div>
            <label htmlFor="estimateDate" className="block text-sm font-medium text-gray-700 mb-1">Estimate Date</label>
            <input type="date" name="estimateDate" id="estimateDate" value={formData.estimateDate} onChange={handleChange} className="mt-1 block w-full input-class" required />
          </div>
          <div>
            <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (Optional)</label>
            <input type="date" name="expiryDate" id="expiryDate" value={formData.expiryDate} onChange={handleChange} className="mt-1 block w-full input-class" />
          </div>
           <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select name="status" id="status" value={formData.status} onChange={handleChange} className="mt-1 block w-full input-class">
                <option value="draft">Draft</option>
                <option value="sent_to_client">Sent to Client</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Line Items</h3>
          {formData.lineItems.map((item, index) => (
            <div key={index} className="grid grid-cols-12 gap-x-3 gap-y-2 items-end p-3 mb-3 border rounded-md">
              <div className="col-span-12 sm:col-span-5">
                <label htmlFor={`itemDesc-${index}`} className="block text-xs font-medium text-gray-600">Description</label>
                <input type="text" name="description" id={`itemDesc-${index}`} value={item.description} onChange={(e) => handleLineItemChange(index, e)} className="mt-1 block w-full input-sm-class" placeholder="Service or Item" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemQty-${index}`} className="block text-xs font-medium text-gray-600">Quantity</label>
                <input type="number" name="quantity" id={`itemQty-${index}`} value={item.quantity} onChange={(e) => handleLineItemChange(index, e)} className="mt-1 block w-full input-sm-class text-right" min="0.01" step="0.01" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemPrice-${index}`} className="block text-xs font-medium text-gray-600">Unit Price</label>
                <input type="number" name="unitPrice" id={`itemPrice-${index}`} value={item.unitPrice} onChange={(e) => handleLineItemChange(index, e)} className="mt-1 block w-full input-sm-class text-right" min="0.00" step="0.01" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemTotal-${index}`} className="block text-xs font-medium text-gray-600">Item Total</label>
                <input type="number" name="total" id={`itemTotal-${index}`} value={item.total.toFixed(2)} readOnly className="mt-1 block w-full input-sm-class text-right bg-gray-50" />
              </div>
              <div className="col-span-12 sm:col-span-1 flex justify-end">
                {formData.lineItems.length > 1 && (
                  <button type="button" onClick={() => removeLineItem(index)} className="text-red-500 hover:text-red-700 p-1 rounded-md text-sm">Remove</button>
                )}
              </div>
            </div>
          ))}
          <button type="button" onClick={addLineItem} className="mt-2 text-blue-600 hover:text-blue-800 font-medium text-sm py-2 px-3 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors">
            + Add Line Item
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 border-t pt-6 mt-6">
            <div>
                <label htmlFor="subTotal" className="block text-sm font-medium text-gray-500">Subtotal</label>
                <p className="mt-1 text-lg font-semibold text-gray-800">${formData.subTotal.toFixed(2)}</p>
            </div>
            <div>
                <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700">Tax Rate (%)</label>
                <input type="number" name="taxRate" id="taxRate" value={formData.taxRate * 100} onChange={(e) => setFormData(prev => ({...prev, taxRate: parseFloat(e.target.value) / 100 }))} className="mt-1 block w-full input-class" step="0.0001"/>
            </div>
            <div>
                <label htmlFor="taxAmount" className="block text-sm font-medium text-gray-500">Tax Amount</label>
                <p className="mt-1 text-lg font-semibold text-gray-800">${formData.taxAmount.toFixed(2)}</p>
            </div>
        </div>
        <div className="text-right mb-6">
            <p className="text-sm font-medium text-gray-500">Total Amount</p>
            <p className="text-2xl font-bold text-brand-text-dark">${formData.totalAmount.toFixed(2)}</p>
        </div>

        <div className="mb-6">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea name="notes" id="notes" rows="3" value={formData.notes} onChange={handleChange} className="mt-1 block w-full input-class"></textarea>
        </div>

        <div className="mb-6">
          <label htmlFor="termsAndConditions" className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions (Optional)</label>
          <textarea name="termsAndConditions" id="termsAndConditions" rows="3" value={formData.termsAndConditions} onChange={handleChange} className="mt-1 block w-full input-class"></textarea>
        </div>
        
        <div className="mt-8 flex justify-end">
          <button 
            type="button" 
            onClick={() => navigate('/admin/estimates')}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg mr-3 transition duration-150 ease-in-out"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition duration-150 ease-in-out disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Create Estimate'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateEstimatePage; 